import _ from "lodash";
import React, { useMemo } from "react";
import classNames from "classnames";
import { connect } from "react-redux";

import { IconButton } from "@toddle-edu/ds-web";
import { ChevronDownOutlined, ChevronUpOutlined } from "@toddle-edu/ds-icons";

import { useI18n } from "Hooks";

import { STUDENT } from "Constants/stringConstants";
import { getDefaultNameSortPreference } from "modules/CommonSelectors";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";

import {
  getFormattedNameForUserType,
  getSubText,
} from "MessagingV2/modules/Utils";
import CurriculumProgramLogo from "AppComponents/CurriculumProgramLogo";

import { isIdEqual } from "Utils";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";
import ConditionalTooltipText from "UIComponents/ConditionalTooltipText";
import { useMetadata } from "../../MetadataProvider";
import { HEADER_TYPE, COHORT_TYPE } from "../../../module/utils";

import classes from "./DynamicList.scss";
import UserTypeCard from "./UserTypeCard";
import CohortSelectionCard from "./CohortSelectionCard";

const getTotalCountSubtext = ({ count, type, t }) => {
  switch (type) {
    case COHORT_TYPE.MEMBERS:
      return t("common:count_members", { count });
    case COHORT_TYPE.CLASSES:
      return t("common:classes_with_count", { count });
    case COHORT_TYPE.GRADES:
      return t("common:grade_with_count_lowercase", { count });
    default:
      return count;
  }
};

const DynamicListHeader = ({
  isCollapsed,
  onToggleCollapse,
  headerParams,
  headerContainerClass,
  type,
}) => {
  const { t } = useI18n(["common"]);

  return (
    <div
      className={classNames(headerContainerClass, classes.headerWrapper)}
      onClick={onToggleCollapse}
      onKeyDown={e => onEnterOrSpaceKeyDown(e, onToggleCollapse)}
      role={onToggleCollapse ? "button" : undefined}
    >
      {headerParams?.type === HEADER_TYPE.SCHOOL ? (
        <div className={classes.schoolHeaderContainer}>
          <CurriculumProgramLogo
            curriculumProgramId={_.get(headerParams, "id")}
            size="medium"
          />
          <div className={classes.schoolHeaderInfo}>
            {_.get(headerParams, "title") ? (
              <ConditionalTooltipText
                text={_.get(headerParams, "title")}
                className={"text-label"}
              />
            ) : null}
            {_.get(headerParams, "totalCount") ? (
              <div
                className={classNames("text-body-s", classes.schoolHeaderCount)}
              >
                {getTotalCountSubtext({
                  count: _.get(headerParams, "totalCount"),
                  type,
                  t,
                })}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {onToggleCollapse && (
        <IconButton
          dsVersion="2.0"
          icon={isCollapsed ? <ChevronDownOutlined /> : <ChevronUpOutlined />}
          onClick={e => {
            e.stopPropagation();
            onToggleCollapse();
          }}
          variant="neutral"
          type="plain"
          size="x-small"
        />
      )}
    </div>
  );
};

const DynamicList = ({
  params = {},
  selectedState = {},
  selectedUsersState = {},
  onAddUsers,
  onRemoveUsers,
  onAddCohort,
  onRemoveCohort,
  prevUsers = [],
  testId,
  isCollapsed = false,
  onToggleCollapse,
  defaultStudentNameSortPreference,
}) => {
  const { t } = useI18n(["common"]);
  const {
    showStudentId,
    showListHeader = true,
    renderCohortSubText,
  } = useMetadata();

  const { user_type: loggedInUserType, id: loggedInUserId } = useMemo(
    () => getUserInfo(),
    []
  );
  const { type, cohorts = [], headerParams } = params;

  const isUserList = type === COHORT_TYPE.MEMBERS;
  const componentTestId = testId ? `${testId}-dynamicList` : undefined;

  const headerContainer = classNames(classes.headerContainer, "text-label-2");

  const prevUsersSet = useMemo(() => new Set(_.map(prevUsers, "id")), [
    prevUsers,
  ]);

  if (_.isEmpty(cohorts)) {
    return null;
  }

  return (
    <div className={classes.container}>
      {showListHeader ? (
        <DynamicListHeader
          isCollapsed={isCollapsed}
          onToggleCollapse={onToggleCollapse}
          headerParams={headerParams}
          headerContainerClass={headerContainer}
          type={type}
        />
      ) : null}

      {!isCollapsed &&
        _.map(cohorts, (cohort, index) => {
          if (isUserList) {
            const user = _.get(cohort, "node", {});
            const isCurrentLoggedInUser = isIdEqual(loggedInUserId, user.id);

            const { id: userId, profileImage } = user;

            let name = getFormattedNameForUserType({
              user,
              orderBy: defaultStudentNameSortPreference,
            });

            if (isCurrentLoggedInUser) {
              name = t("common:you_with_full_name", { fullName: name });
            }

            const isUserChecked = !!_.get(selectedUsersState, [userId], false);

            const familyMembers = _.get(user, "familyMembers", []);
            const isParentChecked = _.every(familyMembers, member =>
              _.get(selectedUsersState, [member.id], false)
            );

            const isParentIndeterminate =
              !isParentChecked &&
              _.some(familyMembers, member =>
                _.get(selectedUsersState, [member.id], false)
              );

            const isParentDisabled = _.every(familyMembers, member =>
              prevUsersSet.has(member.id)
            );

            const isUserDisabled = prevUsersSet.has(userId);
            const studentId = _.get(user, "sourceId", "");

            return (
              <UserTypeCard
                key={userId || index}
                onAddUsers={onAddUsers}
                onRemoveUsers={onRemoveUsers}
                avatarId={userId}
                mainText={name}
                subText={getSubText({
                  loggedInUserRole: loggedInUserType,
                  messagedUserObj: user,
                  messagedUserRole: _.get(user, "type"),
                  t,
                  defaultStudentNameSortPreference,
                  showStudentId:
                    showStudentId &&
                    user.type === STUDENT &&
                    !_.isEmpty(studentId),
                  studentId,
                })}
                user={user}
                parents={familyMembers}
                profileImage={profileImage}
                isUserChecked={isUserChecked}
                isParentChecked={isParentChecked}
                isParentIndeterminate={isParentIndeterminate}
                isParentDisabled={isParentDisabled}
                isUserDisabled={isUserDisabled}
                testId={`${componentTestId}-userTypeCard-${userId}`}
              />
            );
          } else {
            const { userList, id: cohortId, title, type: subType } = cohort;
            const key = `${cohortId}-${subType}`;
            const isChecked = !!_.get(selectedState, [key], false);
            const isIndeterminate = !_.isEmpty(
              _.get(selectedState, [key, "removedUsersIds"], [])
            );

            const subTextComponent = _.isFunction(renderCohortSubText)
              ? renderCohortSubText({ cohort, type: subType, t })
              : null;

            return (
              <CohortSelectionCard
                key={key}
                id={cohortId}
                type={subType}
                title={title}
                cohortType={type}
                onAddCohort={onAddCohort}
                onRemoveCohort={onRemoveCohort}
                userList={userList}
                isChecked={isChecked}
                isIndeterminate={isIndeterminate}
                subTextComponent={subTextComponent}
                testId={`${componentTestId}-cohortSelectionCard-${key}`}
              />
            );
          }
        })}
    </div>
  );
};

const mapStateToProps = state => ({
  defaultStudentNameSortPreference: getDefaultNameSortPreference(state),
});

export default connect(mapStateToProps)(DynamicList);
